import { useState, useEffect, useMemo } from 'react'
import { MoneyCollectOutlined, ShareAltOutlined } from '@ant-design/icons';
import PageHeading from '../../components/PageHeading';
import { debounce } from 'lodash';
import {
  message,
  Table,
  Button,
  Input,
  Select,
  DatePicker
} from 'antd';
import axiosInstance from '../../utils/axios';
import fetchMiniList from '../../utils/miniFetcher.js';
import { dateFormatmicroSecond } from '../../utils/dateFormats';

const { RangePicker } = DatePicker;

const getMediaPath = (fullUrl) => {
  if (!fullUrl) return '';
  try {
    const url = new URL(fullUrl);
    const index = url.pathname.indexOf('mediafiles');
    if (index !== -1) {
      return url.pathname.substring(index);
    }
    return url.pathname;
  } catch (e) {
    const index = fullUrl.indexOf('mediafiles');
    return index !== -1 ? fullUrl.substring(index) : fullUrl;
  }
};

const EventTicketSnapshots = () => {

  const [bookings, setBookings] = useState({
    results: [],
    count: 0
  });

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    id: null,
    event: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchMasters();
  }, []);

  useEffect(() => {
    const delayFetch = debounce(() => {
      fetchData();
    }, 550);

    delayFetch();
    return () => delayFetch.cancel();

    // eslint-disable-next-line
  }, [page, filters]);

  const fetchMasters = async () => {
    try {
      let eventResponse = await fetchMiniList("news_and_events/events/");
      if (!eventResponse.status) {
        eventResponse = await fetchMiniList("news_and_events/events/select");
      }
      if (eventResponse.status) {
        const eventsData = eventResponse.data.results || eventResponse.data;
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } else {
        message.error(eventResponse.data);
      }
    } catch (error) {
      console.error("Error in fetchMasters:", error);
    }
  };

  const shareTicket = async (record) => {
    try {
      await axiosInstance.post(`news_and_events/share-event-ticket/`, record);
      message.success("Mail sent successfully");
    } catch (err) {
      message.error("Cannot send mail");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);

      if (filters.id) params.append("id", filters.id);
      if (filters.event) params.append("event_title__icontains", filters.event);
      if (filters.startDate) params.append("created_at__date__gte", filters.startDate);
      if (filters.endDate) params.append("created_at__date__lte", filters.endDate);

      const response = await axiosInstance.get(
        `news_and_events/event-ticket-snapshots/?${params.toString()}`
      );

      setBookings(response.data);
    } catch (error) {
      message.error("Failed to fetch snapshots");
    } finally {
      setLoading(false);
    }
  };

  /* ============================
      BONUS CALCULATIONS
  ============================ */

  const pageStats = useMemo(() => {
    const data = bookings.results || [];

    const totalAmount = data.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );

    const verifiedCount = data.filter(item => item.is_verified === true).length;
    const nonVerifiedCount = data.filter(item => item.is_verified !== true).length;

    return {
      totalAmount,
      verifiedCount,
      nonVerifiedCount
    };
  }, [bookings]);

  const columns = [
    {
      title: 'Booking ID',
      key: 'id',
      render: (_, record) => record.id,
    },
    {
      title: 'QR Code',
      key: 'qr_code',
      render: (_, record) => {
        const qrSrc = getMediaPath(record.qr_code);
        return (
          <div className="qr-wrapper">
            <button className="qr-share-btn" onClick={() => shareTicket(record)}>
              <ShareAltOutlined />
            </button>
            <img
              src={`${process.env.REACT_APP_MEDIA_URL}${qrSrc}`}
              alt="qr code"
              width={80}
              height={80}
            />
          </div>
        )
      }
    },
    {
      title: 'Event Name',
      key: 'event',
      render: (_, record) =>
        record.event_title || record.event?.heading || '-',
    },
    {
      title: 'Booked at',
      key: 'created_at',
      render: (_, record) =>
        dateFormatmicroSecond(record.created_at),
    },
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) => record.booked_by,
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => `${record.amount}`,
    },
    {
      title: 'Number of Seats',
      key: 'seats',
      render: (_, record) =>
        record.total_tickets || record.number_of_tickets || '-',
    },
    {
      title: 'Verified',
      key: 'status',
      render: (_, record) =>
        record.is_verified === true
          ? <div style={{ padding: "5px", height: "30px", backgroundColor: "green", color: "white" }}>Verified</div>
          : <div style={{ padding: "5px", height: "30px", backgroundColor: "orange", color: "white" }}>Non Verified</div>
    }
  ];

  return (
    <div className='dashboard'>
      <PageHeading name="Event Ticket Snapshots" icon={<MoneyCollectOutlined />} />

      <small>
        In case customer lost ticket or didnt recieve email - (hover qr to share)
      </small>

      {/* FILTERS */}
      <div className="filters" style={{ marginTop: "10px", marginBottom: "10px" }}>
        <Input
          placeholder="Booking ID"
          style={{ width: 150, margin: "3px" }}
          value={filters.id}
          onChange={(e) =>
            setFilters(prev => ({ ...prev, id: e.target.value }))
          }
          allowClear
        />

        <Select
          showSearch
          placeholder="Events"
          style={{ width: 180, margin: "3px" }}
          value={filters.event}
          onChange={(value) =>
            setFilters(prev => ({ ...prev, event: value }))
          }
          options={events.map(e => ({
            label: e.heading || e.name,
            value: e.heading || e.name
          }))}
          allowClear
        />

        <RangePicker
          style={{ margin: "3px" }}
          onChange={(dates) => {
            if (dates) {
              setFilters(prev => ({
                ...prev,
                startDate: dates[0].format("YYYY-MM-DD"),
                endDate: dates[1].format("YYYY-MM-DD")
              }));
            } else {
              setFilters(prev => ({
                ...prev,
                startDate: null,
                endDate: null
              }));
            }
          }}
        />

        <Button
          onClick={() =>
            setFilters({
              id: null,
              event: null,
              startDate: null,
              endDate: null
            })
          }
        >
          Clear
        </Button>
      </div>

      {/* SUMMARY SECTION */}
      <div style={{
        marginBottom: "15px",
        padding: "12px",
        background: "#f5f5f5",
        borderRadius: "8px",
        display: "flex",
        flexWrap: "wrap",
        gap: "20px",
        fontWeight: 500
      }}>
        <div>📊 Total Bookings: {bookings.count || 0}</div>
        <div>📄 Showing: {bookings.results?.length || 0}</div>
        <div>💰 Page Amount: {pageStats.totalAmount.toFixed(2)}</div>
        <div style={{ color: "green" }}>
          ✅ Verified: {pageStats.verifiedCount}
        </div>
        <div style={{ color: "orange" }}>
          ⚠ Non Verified: {pageStats.nonVerifiedCount}
        </div>
      </div>

      <Table
        dataSource={bookings.results}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total: bookings.count,
          onChange: (newPage) => setPage(newPage)
        }}
      />
    </div>
  )
}

export default EventTicketSnapshots